import React from "react";
import { connect } from "react-redux";

import { IAppState } from "../core/state/Store";
import GeneralContainer from "../core/components/containers/GeneralContainer";
import {
    Alignment,
    FlexDirection,
    Side,
    Size,
} from "../core/types/system/Layout";
import { setPageLoading } from "../core/state/system/UIConfiguration";
import List from "../core/components/list/List";
import UserRole from "../core/types/core/UserRole";
import ListItem from "../core/components/list/ListItem";
import {
    initialiseUserRolesDetails,
    updateUserRolesDetails,
} from "../core/state/core/UserRoles";
import { unwrappedDispatch } from "../core/foundation/Dispatching";

import "./UserRoles.scss";
import ProductSubscription from "../core/types/core/ProductSubscription";
import ClientSystem from "../core/types/core/ClientSystem";
import { initialiseProductSubscriptions } from "../core/state/core/ProductSubscriptions";
import { initialiseClientSystems } from "../core/state/core/ClientSystem";
import User from "../core/types/core/User";
import Grid from "../core/components/grid/Grid";
import GridColumn from "../core/components/grid/GridColumn";
import Dialog from "../core/components/dialog/Dialog";
import DialogContent from "../core/components/dialog/DialogContent";
import DialogFooter from "../core/components/dialog/DialogFooter";
import DialogCancel from "../core/components/dialog/DialogCancel";
import DialogOK from "../core/components/dialog/DialogOK";
import SelectorList from "../core/components/selector/SelectorList";

interface RouteUserRolesProps {
    roles: UserRole[];
    systems: ClientSystem[];
    allUsers: User[];
    productSubscriptions: ProductSubscription[];

    setPageLoading: (value: boolean) => void;
    initialiseUserRolesDetails: () => Promise<void>;
    initialiseClientSystems: () => Promise<void>;
    initialiseProductSubscriptions: () => Promise<void>;
    updateUserRolesDetails: (userRoles: UserRole[]) => Promise<void>;
}

interface RouteUserRolesState {
    roles: UserRole[];

    manageOpen: boolean;
    manageRole?: UserRole;
    selectedUsers: User[];
}

function CalcRoleLicenceCount(
    role: UserRole,
    subscriptions: ProductSubscription[],
) {
    return role.appInstances.reduce((prev: number, crnt) => {
        const subscription = subscriptions.find(
            s => s.clientSystemID === crnt.id,
        );
        if (subscription.licenceCount === undefined) return prev;

        if (prev === undefined) return subscription.licenceCount;

        return Math.min(prev, subscription.licenceCount);
    }, undefined);
}

function CalcAppAvailableCount(
    app: ClientSystem,
    subscription: ProductSubscription,
    roles: UserRole[],
) {
    if (subscription.licenceCount === undefined)
        return subscription.licenceCount;
    const users = new Array<number>();
    for (const role of roles)
        if (role.appInstances.includes(app)) users.push(...role.users);
    return subscription.licenceCount - new Set(users).size;
}

function CalcRoleLicenceAvailableCount(
    role: UserRole,
    subscriptions: ProductSubscription[],
    roles: UserRole[],
) {
    return role.appInstances.reduce((prev: number, crnt) => {
        const subscription = subscriptions.find(
            s => s.clientSystemID === crnt.id,
        );
        const available = CalcAppAvailableCount(crnt, subscription, roles);

        if (subscription.licenceCount === undefined) return prev;

        if (prev === undefined) return available;

        return Math.min(prev, available);
    }, undefined);
}

class RouteUserRoles extends React.Component<
    RouteUserRolesProps,
    RouteUserRolesState
> {
    state: RouteUserRolesState = {
        roles: [],
        manageOpen: false,
        selectedUsers: [],
    };

    async componentDidMount(): Promise<void> {
        this.props.setPageLoading(true);
        await Promise.all([
            this.props.initialiseUserRolesDetails(),
            this.props.initialiseClientSystems(),
            this.props.initialiseProductSubscriptions(),
        ]);
        this.props.setPageLoading(false);
    }

    static getDerivedStateFromProps(
        props: RouteUserRolesProps,
        state: RouteUserRolesState,
    ): RouteUserRolesState {
        const valid = props.roles
            .filter(role => role.licenced)
            .map(
                role =>
                    ({
                        ...role,
                        appInstances: props.systems.filter(s =>
                            role.apps.includes(s.id),
                        ),
                        userInstances: props.allUsers.filter(u =>
                            role.users.includes(u.id),
                        ),
                        userCount: role.users.length,
                    } as UserRole),
            );
        return {
            roles: valid.map(role => ({
                ...role,
                shared: valid.some(
                    r =>
                        r.apps.some(app => role.apps.includes(app)) &&
                        r.id !== role.id,
                ),
                licenceCount: CalcRoleLicenceCount(
                    role,
                    props.productSubscriptions,
                ),
                availableCount: CalcRoleLicenceAvailableCount(
                    role,
                    props.productSubscriptions,
                    valid,
                ),
            })),
            manageOpen: state.manageOpen,
            selectedUsers: state.selectedUsers,
        };
    }

    manageUsers(role: UserRole) {
        this.setState(() => ({
            manageOpen: true,
            manageRole: role,
            selectedUsers: role.userInstances,
        }));
    }

    roleUsersChanged(users: User[]) {
        this.setState(() => ({
            selectedUsers: users,
        }));
    }

    roleUsersCanSave() {
        return (
            !this.state.manageRole?.availableCount ||
            this.state.selectedUsers?.length <=
                this.state.manageRole?.availableCount
        );
    }

    async roleUsersSave() {
        try {
            this.props.setPageLoading(true);

            const role = this.state.manageRole;
            role.users = this.state.selectedUsers.map(u => u.id);

            await this.props.updateUserRolesDetails([role]);

            this.setState(prev => ({
                ...prev,
                manageOpen: false,
            }));
        } finally {
            this.props.setPageLoading(false);
        }
    }

    render() {
        return (
            <section>
                <GeneralContainer
                    scaling={true}
                    mid={true}
                    max={true}
                    bufferSide={Side.All}
                    clearanceSide={Side.Top}
                    clearanceSize={Size.Large}
                    alignment={Alignment.Center}>
                    <List>
                        {this.state.roles.map((role, index) => (
                            <ListItem
                                key={index}
                                autoHeight={true}
                                column={true}
                                selectable={false}
                                panel={true}
                                flex={FlexDirection.Row}
                                bufferSide={Side.All}
                                clearanceSide={Side.Bottom}
                                data={role}>
                                <div className="full flex-column spaced">
                                    <div className="col">
                                        <h3>{role.name}</h3>
                                    </div>
                                    <div className="col">
                                        {role.shared && (
                                            <b>
                                                (Licencing shared with other
                                                roles)&nbsp;
                                            </b>
                                        )}
                                        {role.licenceCount && (
                                            <b>
                                                Licensed for {role.licenceCount}
                                                &nbsp; User
                                                {role.licenceCount > 1
                                                    ? "s"
                                                    : ""}{" "}
                                                -&nbsp;
                                            </b>
                                        )}
                                        {!role.licenceCount && (
                                            <b>Unlimited Licence - </b>
                                        )}
                                        <b>{role.userCount} Assigned</b>
                                        {role.licenceCount && (
                                            <b>
                                                , {role.availableCount}{" "}
                                                Remaining
                                            </b>
                                        )}
                                    </div>
                                </div>
                                <div className="top-buffer left full clearfix flex-row">
                                    <div className="col">
                                        {role.description}
                                    </div>
                                    <button
                                        className="col-pane no-print"
                                        style={{
                                            minWidth: "150px",
                                            marginLeft: "10px",
                                        }}
                                        onClick={() => this.manageUsers(role)}>
                                        Manage Users
                                    </button>
                                </div>
                                <div className="top-buffer left full clearfix">
                                    {role.userInstances.length > 0 ? (
                                        <Grid<User>
                                            value={role.userInstances}
                                            keyField="id"
                                            fullWidth={true}
                                            fixedHeader={true}
                                            maxHeight="200px"
                                            defaultSortField="username">
                                            <GridColumn
                                                header="Name"
                                                field="fullName"
                                            />
                                            <GridColumn
                                                header="Teacher Code"
                                                field="teacherCode"
                                                width="150px"
                                            />
                                            <GridColumn
                                                header="Student Code"
                                                field="studentCode"
                                                width="150px"
                                            />
                                        </Grid>
                                    ) : (
                                        <div className="full panel buffer inset contain">
                                            <div className="">
                                                No users have access
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </ListItem>
                        ))}
                    </List>
                </GeneralContainer>

                <Dialog
                    showDialog={this.state.manageOpen}
                    dialogIcon="assignment_ind"
                    dialogTitle={
                        this.state.manageRole?.name || "Manage Role Users"
                    }
                    width={1000}>
                    <DialogContent>
                        <SelectorList<User>
                            source={this.props.allUsers}
                            value={this.state.selectedUsers}
                            onChange={this.roleUsersChanged.bind(this)}
                            keyField="id">
                            <GridColumn
                                header="Name"
                                field="fullName"
                            />
                            <GridColumn
                                header="Teacher Code"
                                field="teacherCode"
                                width="50px"
                            />
                            <GridColumn
                                header="Student Code"
                                field="studentCode"
                                width="50px"
                            />
                        </SelectorList>
                    </DialogContent>
                    <DialogFooter>
                        <DialogCancel
                            onClick={() =>
                                this.setState(prev => ({
                                    ...prev,
                                    manageOpen: false,
                                }))
                            }
                        />
                        <DialogOK
                            text="Save"
                            disabled={!this.roleUsersCanSave()}
                            onClick={this.roleUsersSave.bind(this)}
                        />
                    </DialogFooter>
                </Dialog>
            </section>
        );
    }
}

const mapStateToProps = (state: IAppState) => {
    const { user, userRoles, clientSystem, productSubscription } = state.db;
    return {
        roles: userRoles.userRoles,
        systems: clientSystem.clientSystems,
        allUsers: user.allUsers,
        productSubscriptions: productSubscription.productSubscriptions,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        setPageLoading: (value: boolean) => dispatch(setPageLoading(value)),
        initialiseUserRolesDetails: () =>
            unwrappedDispatch(initialiseUserRolesDetails()),
        initialiseClientSystems: () => initialiseClientSystems(),
        initialiseProductSubscriptions: () =>
            unwrappedDispatch(initialiseProductSubscriptions()),
        updateUserRolesDetails: (userRoles: UserRole[]) =>
            unwrappedDispatch(updateUserRolesDetails(userRoles)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(RouteUserRoles);
